import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { settingsPath, settingsRouteFromPathname } from "@/lib/settingsRoutes";
export type ActiveApp = "actions" | "codesmith";

/**
 * Which top-level app the current route belongs to. Codesmith owns the
 * sessions/assistant/automations/MCP connections routes (org-prefixed and
 * legacy bare variants); everything else — runs, monitors, observability, storage,
 * usage, settings, admin — is the GitHub Actions app.
 */
export function useActiveApp() {
  const router = useRouter();
  return isCodesmithPath(router.pathname) ? "codesmith" : "actions";
}
export function isCodesmithPath(pathname: string): boolean {
  return /^(\/\[org\])?\/(sessions|assistant|automations|projects|mcp-connections|codesmith)(\/|$)/.test(pathname);
}
export function settingsPagePath(org: string | null | undefined, isCodesmithPage: boolean): string {
  return settingsPath(org, isCodesmithPage ? "codesmith" : "general");
}

/**
 * Account-level pages that render full-bleed with only the app-switcher rail —
 * no contextual (second-tier) sidebar. Usage/billing, settings, support, and
 * the admin surface don't belong to either app's navigation, so the GitHub
 * Actions sidebar is omitted there. Settings gets its own contextual sidebar
 * in the two-sidebar layout (see SettingsSidebar).
 */
export function isStandaloneAccountPath(pathname: string): boolean {
  return /^(\/\[org\])?\/(usage|support)$/.test(pathname) || isSettingsPath(pathname) || isAdminPath(pathname);
}

/** The settings routes (`/settings/**`, `/[org]/settings/**`). */
export function isSettingsPath(pathname: string): boolean {
  return settingsRouteFromPathname(pathname) !== null;
}

/** Superuser admin routes (`/admin`, `/admin/...`). Never org-prefixed. */
export function isAdminPath(pathname: string): boolean {
  return /^\/admin(\/|$)/.test(pathname);
}